// apps/web/src/components/painel/home/esteira.tsx — Fase 17.8, Plano 08 (D-12; UI-SPEC U-06, U-24, U-26).
// Esteira "Fluxo agora": uma <li> por etapa (de 3 a 7), cada uma um link para a lista de origem, com o círculo de contagem, o rótulo
// e, no gargalo (no máximo um), o texto "gargalo" além da cor âmbar. O tom de cada etapa vem de `estiloDaEtapa` (misturas de
// var(--perfil-*)); a última etapa (concluída) usa o gradiente --done-*. Zero, indisponível e sem medição são distintos: zero mostra
// 0, indisponível mostra AlertCircle e "indisponível", sem medição mostra "—" e "sem medição" (os dois com anel tracejado e fora da
// nota). A nota do gargalo fica sob a esteira e `children` (a assinatura do perfil) vem 32px depois dela.
import { useId, type ReactElement, type ReactNode } from 'react';
import { AlertCircle, ChevronRight, Layers, type LucideIcon } from 'lucide-react';
import type { EsteiraEtapa, RegiaoEsteira } from '@pieltec/contracts';
import { podeAbrirRota } from '../../../lib/painel/blocos';
import { atributosDoBrilho } from '../../../lib/painel/home/brilho';
import { estiloDaEtapa, partesDaNota, rotuloUnidade } from '../../../lib/painel/home/esteira-tom';
import { EstadoVazio } from './estado-vazio';
import { NumeroAoVivo, type OrigemNumero } from './numero-ao-vivo';
import { RegiaoCarregando, RegiaoErro } from './regiao-estado';
import '../../../styles/painel-home.css';

export const TEXTO_ESTEIRA_VAZIA = 'Nenhum lote em produção agora.';
export const TEXTO_ESTEIRA_SEM_ETAPAS = 'Nenhuma etapa do Kanban cadastrada.';

/** Altura do esqueleto: cabeçalho de 56px, a linha de etapas (círculo + rótulo) e a nota. */
const ALTURA_ESQUELETO = 160;

export interface LinkCabecalho {
  readonly rotulo: string;
  readonly href: string;
}

export interface EsteiraProps {
  readonly titulo: string;
  /** Link do cabeçalho (ex.: "Abrir Kanban"); só aparece quando a sessão pode abrir a rota. */
  readonly linkCabecalho?: LinkCabecalho;
  /** `null` enquanto a carga está em andamento (esqueleto). */
  readonly regiao: RegiaoEsteira | null;
  readonly permissoes: readonly string[];
  readonly onTentarNovamente?: () => void;
  /** Assinatura do perfil (ex.: Cabines), renderizada 32px abaixo da nota. */
  readonly children?: ReactNode;
  /** Origem da mudança das contagens: o rolo de dígitos só roda em 'ao-vivo' (as demais trocam em silêncio). */
  readonly origem?: OrigemNumero;
  /** Ids de card que brilham agora (`etapa-{id}`). */
  readonly brilhos?: readonly string[];
  /** Movimento reduzido: contorno estático em vez do brilho animado. */
  readonly reduzido?: boolean;
  /** Texto da linha amigável quando toda etapa medida está zerada (padrão: o do Kanban, "Nenhum lote em produção agora."). */
  readonly textoVazio?: string;
  /**
   * Frase do gargalo: `concentra` ("A **Cura** concentra a fila: ...", PCP) ou `gargalo` ("Gargalo em **Aprovação**: ...", etapas sem
   * artigo natural, como as do Faturamento).
   */
  readonly estiloNota?: 'concentra' | 'gargalo';
  /** Ícone da linha amigável de esteira zerada (padrão Layers). */
  readonly iconeVazio?: LucideIcon;
  /** Limite de "parado" citado na nota do gargalo (padrão '24 h'; o abastecimento usa '60 min'). */
  readonly limiteParado?: string;
}

/** Nome acessível da etapa: "{etapa}: {n} {unidade}. Abrir lista." (indisponível e sem medição dizem o estado, nunca um número). */
export function nomeAcessivelDaEtapa(etapa: EsteiraEtapa): string {
  if (etapa.contagem.estado === 'MEDIDO') return `${etapa.rotulo}: ${rotuloUnidade(etapa.unidade, etapa.contagem.total)}. Abrir lista.`;
  if (etapa.contagem.estado === 'INDISPONIVEL') return `${etapa.rotulo}: indisponível. Abrir lista.`;
  return `${etapa.rotulo}: sem medição. Abrir lista.`;
}

/** Só uma esteira com todas as etapas medidas e zeradas é "sem lotes" (indisponível ou sem medição nunca viram "vazio"). */
export function esteiraSemLotes(etapas: readonly EsteiraEtapa[]): boolean {
  return etapas.length > 0 && etapas.every((etapa) => etapa.contagem.estado === 'MEDIDO' && etapa.contagem.total === 0);
}

interface EtapaProps {
  readonly etapa: EsteiraEtapa;
  readonly indice: number;
  readonly total: number;
  readonly permissoes: readonly string[];
  readonly origem: OrigemNumero;
  readonly brilhos?: readonly string[];
  readonly reduzido: boolean;
}

const formatarContagem = (valor: number): string => valor.toLocaleString('pt-BR');

function Etapa({ etapa, indice, total, permissoes, origem, brilhos, reduzido }: EtapaProps): ReactElement {
  const idGargalo = useId();
  const estado = etapa.contagem.estado;
  const gargalo = etapa.gargalo === true;
  const circulo = (
    <span className="painel-etapa__circulo" data-estado={estado}>
      {estado === 'MEDIDO' && etapa.contagem.estado === 'MEDIDO' && <NumeroAoVivo valor={etapa.contagem.total} formatar={formatarContagem} origem={origem} nomeAcessivelNoPai />}
      {estado === 'INDISPONIVEL' && <AlertCircle className="painel-etapa__icone size-4" aria-hidden="true" />}
      {estado === 'SEM_MEDICAO' && <span aria-hidden="true">—</span>}
    </span>
  );
  const corpo = (
    <>
      {circulo}
      <span className="painel-etapa__rotulo">{etapa.rotulo}</span>
      {estado === 'INDISPONIVEL' && <span className="painel-etapa__estado">indisponível</span>}
      {estado === 'SEM_MEDICAO' && <span className="painel-etapa__estado">sem medição</span>}
      {gargalo && (
        <span id={idGargalo} className="painel-etapa__gargalo">
          gargalo
        </span>
      )}
    </>
  );
  return (
    <li
      className="painel-etapa"
      data-testid={`painel-etapa-${etapa.id}`}
      data-estado={estado}
      data-concluida={etapa.concluida === true ? 'true' : undefined}
      data-gargalo={gargalo ? 'true' : undefined}
      style={estiloDaEtapa(indice, total)}
      {...atributosDoBrilho(`etapa-${etapa.id}`, brilhos, reduzido)}
    >
      {podeAbrirRota(etapa.href, permissoes) ? (
        <a className="painel-etapa__link" href={etapa.href} aria-label={nomeAcessivelDaEtapa(etapa)} aria-describedby={gargalo ? idGargalo : undefined}>
          {corpo}
        </a>
      ) : (
        <div className="painel-etapa__link" role="group" aria-label={nomeAcessivelDaEtapa(etapa).replace(' Abrir lista.', '')}>
          {corpo}
        </div>
      )}
    </li>
  );
}

/** Nota do gargalo: "A **{etapa}** concentra a fila: {n} lotes parados há mais de 24 h, vindos de {origens}." */
function Nota({ regiao, estilo, limiteParado }: { readonly regiao: Extract<RegiaoEsteira, { estado: 'MEDIDO' }>; readonly estilo: 'concentra' | 'gargalo'; readonly limiteParado?: string }): ReactElement | null {
  if (regiao.nota === undefined) return null;
  const unidade = regiao.etapas.find((etapa) => etapa.rotulo === regiao.nota?.etapa)?.unidade ?? 'lotes';
  const partes = partesDaNota(regiao.nota, unidade, estilo, limiteParado);
  return (
    <p className="painel-esteira__nota" data-testid="painel-esteira-nota">
      {estilo === 'gargalo' ? 'Gargalo em ' : 'A '}
      <b>{partes.etapa}</b>
      {partes.resto}
    </p>
  );
}

export function Esteira({ titulo, linkCabecalho, regiao, permissoes, onTentarNovamente, children, origem = 'silenciosa', brilhos, reduzido = false, textoVazio = TEXTO_ESTEIRA_VAZIA, estiloNota = 'concentra', iconeVazio = Layers, limiteParado }: EsteiraProps): ReactElement {
  const idTitulo = useId();
  const cabecalho = (
    <header className="painel-lista__cabecalho">
      <h2 id={idTitulo} className="painel-lista__titulo">
        <span className="painel-lista__marcador" aria-hidden="true" />
        {titulo}
      </h2>
      {linkCabecalho !== undefined && podeAbrirRota(linkCabecalho.href, permissoes) && (
        <a className="painel-esteira__abrir" href={linkCabecalho.href}>
          {linkCabecalho.rotulo}
          <ChevronRight className="size-4" aria-hidden="true" />
        </a>
      )}
    </header>
  );

  if (regiao === null) {
    return (
      <section className="painel-esteira" data-testid="painel-esteira" aria-labelledby={idTitulo} aria-busy="true">
        {cabecalho}
        <RegiaoCarregando id="esteira" altura={ALTURA_ESQUELETO} />
      </section>
    );
  }

  let corpo: ReactElement;
  if (regiao.estado === 'INDISPONIVEL') {
    corpo = (
      <div className="painel-lista__erro">
        <RegiaoErro id="esteira" nome={`a esteira "${titulo}"`} onTentarNovamente={onTentarNovamente ?? (() => undefined)} />
      </div>
    );
  } else if (regiao.estado === 'SEM_MEDICAO') {
    corpo = <EstadoVazio icone={Layers} texto={TEXTO_ESTEIRA_SEM_ETAPAS} />;
  } else if (esteiraSemLotes(regiao.etapas)) {
    corpo = <EstadoVazio icone={iconeVazio} texto={textoVazio} />;
  } else {
    corpo = (
      <div className="painel-esteira__corpo">
        <ol className="painel-esteira__etapas" aria-labelledby={idTitulo}>
          {regiao.etapas.map((etapa, indice) => (
            <Etapa key={etapa.id} etapa={etapa} indice={indice} total={regiao.etapas.length} permissoes={permissoes} origem={origem} brilhos={brilhos} reduzido={reduzido} />
          ))}
        </ol>
        <Nota regiao={regiao} estilo={estiloNota} limiteParado={limiteParado} />
      </div>
    );
  }

  return (
    <section className="painel-esteira" data-testid="painel-esteira" aria-labelledby={idTitulo}>
      {cabecalho}
      {corpo}
      {children !== undefined && <div className="painel-esteira__assinatura">{children}</div>}
    </section>
  );
}
